import type { CofQuizQuestion } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { CircleOfFifths } from '../scale/circle-of-fifths.js'
import styles from './cof-quiz-question.module.css'

export interface CofQuizQuestionPanelProps {
  question: CofQuizQuestion
  scoreCorrect: number
  scoreAnswered: number
  interactive: boolean
  onHearIt: () => void
  onAnswer: (picked: { name: string; ring: 'major' | 'minor' }) => void
}

const QUESTIONS_PER_SESSION = 10

const buildPrompt = (q: CofQuizQuestion): string => {
  if (q.kind === 'find-relative') {
    return q.referenceKey.ring === 'major'
      ? `Find the relative minor of ${q.referenceKey.name} major`
      : `Find the relative major of ${q.referenceKey.name} minor`
  }
  return `Find a key closely related to ${q.referenceKey.name} major`
}

/**
 * CoF Wheel v1 Quiz "asking" surface. Prompt + audition replay + click-on-
 * wheel answer mechanism. The wheel is interactive iff `interactive=true`;
 * the feedback phase re-renders this same panel with `interactive=false`
 * so the user can see the question they answered without re-triggering a
 * new answer.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §9
 */
export const CofQuizQuestionPanel: FC<CofQuizQuestionPanelProps> = props => {
  const { question, scoreCorrect, scoreAnswered, interactive, onHearIt, onAnswer } = props
  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <span className={styles.score}>
          {scoreCorrect} / {QUESTIONS_PER_SESSION}
        </span>
        <span>
          Q {Math.min(scoreAnswered + 1, QUESTIONS_PER_SESSION)} / {QUESTIONS_PER_SESSION}
        </span>
      </header>
      <p className={styles.prompt}>{buildPrompt(question)}</p>
      <Button variant="outlined" size="small" onClick={onHearIt} className={styles.hearBtn}>
        Hear it
      </Button>
      <div className={styles.wheel}>
        <CircleOfFifths onKeySelect={interactive ? onAnswer : undefined} />
      </div>
    </div>
  )
}
